import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Clock, User, FileText, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { auditApi } from '../services/api';
import { toast } from 'sonner';

const ACTION_LABELS = {
  BUDGET_SAVE: { label: 'Budget Save', color: 'bg-blue-100 text-blue-700' },
  LINE_DELETE: { label: 'Line Delete', color: 'bg-red-100 text-red-700' },
  BUDGET_APPROVE: { label: 'Budget Approve', color: 'bg-green-100 text-green-700' },
  CUSTOMER_MERGE: { label: 'Customer Merge', color: 'bg-purple-100 text-purple-700' },
  DIVISION_CHANGE: { label: 'Division Change', color: 'bg-orange-100 text-orange-700' },
  CUSTOMER_TRANSFER: { label: 'Customer Transfer', color: 'bg-cyan-100 text-cyan-700' },
};

const formatTimestamp = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return ts;
  }
};

const getDetailSummary = (action, details) => {
  if (!details) return '';
  switch (action) {
    case 'BUDGET_SAVE':
      return `${details.pipeline || ''} - ${details.product_group || ''} ${details.product_name ? `/ ${details.product_name}` : ''}`.trim();
    case 'LINE_DELETE':
      return `${details.pipeline || ''} - ${details.product_code || ''}`;
    case 'BUDGET_APPROVE':
      return `Approved by ${details.approved_by_name || details.approved_by_emp_id || ''}`;
    case 'CUSTOMER_MERGE':
      return `Merged ${details.merged_count || 0} customer(s), ${details.entries_updated || 0} entries`;
    case 'DIVISION_CHANGE':
      return `${details.old_division || '?'} → ${details.new_division || '?'}`;
    case 'CUSTOMER_TRANSFER':
      return `${details.old_manager || '?'} → ${details.new_manager_name || '?'}`;
    default:
      return JSON.stringify(details).slice(0, 80);
  }
};

export const AuditLog = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAdmin } = useAuth();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionTypes, setActionTypes] = useState([]);

  // Filters
  const [actionFilter, setActionFilter] = useState('all');
  const [custCodeFilter, setCustCodeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchLogs = useCallback(async () => {
    if (authLoading) return;
    setLoading(true);
    try {
      const filters = { page, page_size: 30 };
      if (actionFilter !== 'all') filters.action = actionFilter;
      if (custCodeFilter.trim()) filters.cust_code = custCodeFilter.trim();
      if (userFilter !== 'all' && isAdmin) filters.user_emp_id = userFilter;
      if (fromDate) filters.from_date = new Date(fromDate).toISOString();
      if (toDate) filters.to_date = new Date(toDate + 'T23:59:59').toISOString();

      const data = await auditApi.getLogs(filters);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, custCodeFilter, userFilter, fromDate, toDate, authLoading, isAdmin]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    auditApi.getActions().then(setActionTypes).catch(() => {});
  }, []);

  const clearFilters = () => {
    setActionFilter('all');
    setCustCodeFilter('');
    setUserFilter('all');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-8" data-testid="audit-log-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-[57px] z-30">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900 -ml-2" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-600" />
                Audit Log
              </h1>
              <p className="text-xs text-gray-500">{total} total events</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 pt-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40 h-8 text-xs bg-gray-50 border-gray-200" data-testid="action-filter">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map(a => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a]?.label || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Customer Code"
                value={custCodeFilter}
                onChange={(e) => { setCustCodeFilter(e.target.value); setPage(1); }}
                className="w-32 h-8 text-xs"
                data-testid="cust-code-filter"
              />
            </div>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} className="w-36 h-8 text-xs" data-testid="from-date-filter" />
            <span className="text-xs text-gray-400">to</span>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} className="w-36 h-8 text-xs" data-testid="to-date-filter" />
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-gray-500 hover:text-gray-700">Clear</Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="audit-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase border-b border-gray-200">Timestamp</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase border-b border-gray-200">Action</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase border-b border-gray-200">User</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase border-b border-gray-200">Customer</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-600 uppercase border-b border-gray-200">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                      No audit events found
                    </td>
                  </tr>
                ) : logs.map((log, i) => {
                  const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={i} className="hover:bg-gray-50 border-b border-gray-100" data-testid={`audit-row-${i}`}>
                      <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-2">
                        <Badge className={`text-[9px] ${actionInfo.color}`}>{actionInfo.label}</Badge>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className="h-5 w-5 rounded-full bg-zinc-800 flex items-center justify-center">
                            <User className="h-3 w-3 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900">{log.user_name || log.user_emp_id}</p>
                            <p className="text-[10px] text-gray-400">{log.user_emp_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-xs text-gray-900">{log.customer_name || log.cust_code || '-'}</p>
                        {log.cust_code && <p className="text-[10px] text-gray-400">{log.cust_code}</p>}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-[300px] truncate">
                        {getDetailSummary(log.action, log.details)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Page {page} of {totalPages} ({total} events)
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs" data-testid="prev-page-btn">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs" data-testid="next-page-btn">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
